/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown } from "heroicons-react";
import { AvatarDropdown } from "./AvatarDropdown";
import { Logo } from "./Logo";
import { NavLink, Link, useHistory, useLocation } from "react-router-dom";
import { routes } from "../routes";
import useProjects, { useCurrentProject } from "../hooks/useProjects";
import { useAuth } from "../context/auth-context";
import { ProjectSwitch } from "./ProjectSwitch";
import { Project } from "../utils/api/types";
import { keywordMapPath } from "../utils/keyword-map-path";
import { usableProjectId } from "../utils/project-id";
import {
  MODULE_GROUPS,
  moduleForPath,
  moduleHref,
  modulesForGroup,
  ModuleGroupId,
  ModuleNavItem,
} from "../utils/module-nav";
import { useQueryCache } from "react-query";
import { useKeywordMapList } from "../hooks/useKeywordMap";

const navProjectMenuClass = "relative w-56 flex-shrink-0 mr-6";
const navProjectButtonClass =
  "relative w-full py-1.5 pl-3 pr-10 border border-gray-300 bg-white rounded-md shadow-sm text-left focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm";
const navProjectListClass =
  "absolute z-50 right-0 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white py-1 text-sm shadow-lg";

export function pathForProject(
  pathname: string,
  currentProjectId: string | undefined,
  nextProjectId: string,
  nextProject: Project | undefined
): string | null {
  if (pathname.includes("/keyword-map/")) {
    return keywordMapPath({ ...nextProject, id: nextProjectId });
  }

  const detailLists: Array<[RegExp, string]> = [
    [/^\/content-optimization\/detail\//, routes.listContentOptimization.path],
    [/^\/api-schedule\/detail\//, routes.listAPISchedule.path],
    [/^\/optimization-wizard\/detail\//, routes.listOptimizationWizard.path],
    [/^\/content-decay\/detail\//, routes.listContentDecay.path],
    [/^\/traffic-trend\/detail\//, routes.trafficTrend.path],
    [/^\/traffic-trend\/create\//, routes.trafficTrend.path],
    [/^\/traffic-insights\/detail\//, routes.listTrafficInsights.path],
  ];
  for (const [pattern, listPath] of detailLists) {
    if (pattern.test(pathname)) {
      return listPath.replace(":projectId", nextProjectId);
    }
  }

  if (
    usableProjectId(currentProjectId) &&
    usableProjectId(nextProjectId) &&
    pathname.includes(`/${currentProjectId}`)
  ) {
    return pathname.replace(`/${currentProjectId}`, `/${nextProjectId}`);
  }

  return null;
}

type ResolvedModule = ModuleNavItem & { to: string };

const desktopLinkClass =
  "inline-flex items-center px-1 pt-1 border-b-2 border-transparent text-sm font-medium leading-5 text-gray-900 focus:outline-none focus:border-indigo-700 transition duration-150 ease-in-out";
const mobileLinkClass =
  "block border-l-4 border-transparent pl-3 pr-4 py-2 text-base font-medium text-gray-600 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800 focus:border-gray-300 focus:bg-gray-50 focus:text-gray-800 focus:outline-none transition duration-150 ease-in-out";
const mobileActiveClass = "border-indigo-500 bg-indigo-50 text-indigo-700";
const mobileGroupLabelClass =
  "px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wider text-gray-400";
const desktopMenuClass =
  "absolute left-0 top-full z-50 w-56 rounded-md border border-gray-200 bg-white py-1 shadow-lg";
const desktopMenuLinkClass =
  "block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none transition duration-150 ease-in-out";

type DesktopNavGroupProps = {
  groupId: ModuleGroupId;
  label: string;
  reports: ResolvedModule[];
  active: boolean;
  className: string;
  open: boolean;
  onOpen: (groupId: ModuleGroupId) => void;
  onClose: (groupId: ModuleGroupId) => void;
};

// The group label stays a link to the first report. Its submenu replaces the
// second navigation row and stays open while the pointer or keyboard focus is
// inside the group. A touch screen has no hover, so the first tap only opens.
const DesktopNavGroup: React.FC<DesktopNavGroupProps> = ({
  groupId,
  label,
  reports,
  active,
  className,
  open,
  onOpen,
  onClose,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const armTouchOpen = useRef(false);
  const menuId = `nav-group-${groupId}`;
  const show = useCallback(() => onOpen(groupId), [onOpen, groupId]);
  const hide = useCallback(() => onClose(groupId), [onClose, groupId]);

  useEffect(() => {
    const node = rootRef.current;
    if (!node) {
      return;
    }
    const onFocusIn = () => show();
    const onFocusOut = (event: FocusEvent) => {
      if (event.relatedTarget instanceof Node && node.contains(event.relatedTarget)) {
        return;
      }
      hide();
    };
    node.addEventListener("focusin", onFocusIn);
    node.addEventListener("focusout", onFocusOut);
    return () => {
      node.removeEventListener("focusin", onFocusIn);
      node.removeEventListener("focusout", onFocusOut);
    };
  }, [show, hide]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const onPointerDown = (event: PointerEvent) => {
      if (
        rootRef.current &&
        event.target instanceof Node &&
        rootRef.current.contains(event.target)
      ) {
        return;
      }
      hide();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        hide();
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, hide]);

  if (reports.length === 0) {
    return null;
  }
  const firstReport = reports[0];

  return (
    <div
      ref={rootRef}
      className={className}
      onMouseOver={show}
      onMouseOut={(event) => {
        if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) {
          return;
        }
        hide();
      }}
    >
      <NavLink
        to={firstReport.to}
        isActive={() => active}
        activeClassName="border-indigo-500"
        className={desktopLinkClass}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={menuId}
        onTouchStart={() => {
          if (!open) {
            armTouchOpen.current = true;
            show();
          }
        }}
        onClick={(event) => {
          if (!armTouchOpen.current) {
            return;
          }
          event.preventDefault();
          armTouchOpen.current = false;
        }}
      >
        {label}
        <ChevronDown className="ml-1 h-4 w-4 text-gray-400" aria-hidden="true" />
      </NavLink>
      <nav
        id={menuId}
        aria-label={`${label} reports`}
        hidden={!open}
        className={desktopMenuClass}
      >
        {reports.map((report) => (
          <NavLink
            key={report.id}
            to={report.to}
            isActive={(_match, loc) => loc.pathname.includes(report.match)}
            activeClassName="bg-indigo-50 text-indigo-700"
            className={desktopMenuLinkClass}
            onClick={hide}
          >
            {report.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
};

export const Navigation: React.FC = () => {
  const { project } = useCurrentProject();
  const { data: projects } = useProjects();
  const { user, logout } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const queryCache = useQueryCache();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openGroupId, setOpenGroupId] = useState<ModuleGroupId | null>(null);
  const openGroup = useCallback((groupId: ModuleGroupId) => {
    setOpenGroupId(groupId);
  }, []);
  const closeGroup = useCallback((groupId: ModuleGroupId) => {
    setOpenGroupId((current) => (current === groupId ? null : current));
  }, []);
  const { data: keywordMaps } = useKeywordMapList({
    projectId:
      user?.accounts?.[0]?.account_type === "paid" && project?.key_map === "yes"
        ? project.id
        : "",
    refetchWhilePending: false,
  });

  const onProjectChange = (nextProjectId: string) => {
    const nextProject = (projects ?? []).find((item) => item.id === nextProjectId);
    const nextPath = pathForProject(location.pathname, project?.id, nextProjectId, nextProject);
    if (!nextPath) {
      return;
    }
    if (nextPath.includes("#")) {
      history.push(nextPath);
      return;
    }
    history.push(nextPath + location.search + location.hash);
  };

  const projectId = usableProjectId(project?.id);
  const projectCount = projects?.length ?? 0;
  const isPaid = user?.accounts?.[0]?.account_type === "paid";
  const keywordMapHref = keywordMapPath(project, keywordMaps);
  const resolveModule = (item: ModuleNavItem): ResolvedModule => ({
    ...item,
    to: moduleHref(item.id, projectId, keywordMapHref),
  });
  const currentModule = moduleForPath(location.pathname, isPaid);

  const closeMobileMenu = () => setMobileMenuOpen(false);

  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenGroupId(null);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (!mobileMenuOpen) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [mobileMenuOpen]);

  const handleLogout = (event: React.SyntheticEvent) => {
    event.preventDefault();
    logout();
    queryCache.clear({});
    history.replace("/login");
  };

  // Phone: the bar stays at the top while the page scrolls. Desktop stays in flow,
  // above in-page panels so an open group menu is not covered.
  return (
    <nav className="sticky top-0 z-30 bg-white shadow-sm sm:relative sm:top-auto sm:z-40">
      <div className="mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
              <Link to={routes.dashboard.path}>
                <Logo />
              </Link>
            </div>
            <div className="hidden sm:ml-6 sm:flex">
              {MODULE_GROUPS.map((group, index) => (
                <DesktopNavGroup
                  key={group.id}
                  groupId={group.id}
                  label={group.label}
                  reports={modulesForGroup(group.id, isPaid, projectCount).map(resolveModule)}
                  active={currentModule?.group === group.id}
                  className={`${index === 0 ? "" : "ml-8 "}relative flex`}
                  open={openGroupId === group.id}
                  onOpen={openGroup}
                  onClose={closeGroup}
                />
              ))}
            </div>
          </div>
          <div className="hidden sm:ml-6 sm:flex sm:items-center">
            <ProjectSwitch
              menuClassName={navProjectMenuClass}
              buttonClassName={navProjectButtonClass}
              listClassName={navProjectListClass}
              onChange={onProjectChange}
            />
            <a
              title="Contact the support team"
              className="p-1 border-2 border-transparent text-gray-400 rounded-full hover:text-gray-500 focus:outline-none focus:text-gray-500 focus:bg-gray-100 transition duration-150 ease-in-out"
              aria-label="email"
              href={`mailto:support@searchanalyzer.io?subject=searchanalyzer%20feedback&body=Hi%20Stephan%2C%0D%0A%0D%0AI'm%20currently%20on%20${window.location.href}%20and%20have%20the%20following%20feedback%20to%20share%20with%20you%3A`}
            >

              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
              </svg>


              {/*<svg
                className="h-6 w-6"
                stroke="currentColor"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                />
              </svg>*/}
            </a>
            <div className="ml-3 ">
              <AvatarDropdown />
            </div>
          </div>
          <div className="-mr-2 flex items-center sm:hidden">
            <button
              type="button"
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:bg-gray-100 focus:text-gray-500 transition duration-150 ease-in-out"
              aria-label={mobileMenuOpen ? "Close main menu" : "Open main menu"}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-menu"
              onClick={() => setMobileMenuOpen((open) => !open)}
            >
              <svg
                className={`${mobileMenuOpen ? "hidden" : "block"} h-6 w-6`}
                stroke="currentColor"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
              <svg
                className={`${mobileMenuOpen ? "block" : "hidden"} h-6 w-6`}
                stroke="currentColor"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      <div
        id="mobile-menu"
        className={`${mobileMenuOpen ? "block" : "hidden"} max-h-screen overflow-y-auto sm:hidden`}
      >
        {project?.id ? (
          <div className="border-b border-gray-200 px-4 py-3">
            <ProjectSwitch
              menuClassName="relative w-full"
              buttonClassName="relative w-full py-2 pl-3 pr-10 border border-gray-300 bg-white rounded-md shadow-sm text-left text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              listClassName="absolute z-50 left-0 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white py-1 text-sm shadow-lg"
              onChange={onProjectChange}
            />
          </div>
        ) : null}
        <div className="pt-2 pb-3">
          {MODULE_GROUPS.map((group) => {
            const reports = modulesForGroup(group.id, isPaid, projectCount).map(resolveModule);
            if (reports.length === 0) {
              return null;
            }
            return (
              <div key={group.id}>
                <p className={mobileGroupLabelClass}>{group.label}</p>
                {reports.map((report) => (
                  <NavLink
                    key={report.id}
                    to={report.to}
                    isActive={(_match, loc) => loc.pathname.includes(report.match)}
                    activeClassName={mobileActiveClass}
                    className={`mt-1 ${mobileLinkClass}`}
                    onClick={closeMobileMenu}
                  >
                    {report.label}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </div>
        <div className="border-t border-gray-200 pt-4 pb-3">
          <a
            className="block px-4 py-2 text-base font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800 focus:bg-gray-100 focus:text-gray-800 focus:outline-none transition duration-150 ease-in-out"
            href={`mailto:support@searchanalyzer.io?subject=searchanalyzer%20feedback&body=Hi%20Stephan%2C%0D%0A%0D%0AI'm%20currently%20on%20${window.location.href}%20and%20have%20the%20following%20feedback%20to%20share%20with%20you%3A`}
          >
            Contact support
          </a>
          <Link
            to={routes.account.path}
            className="mt-1 block px-4 py-2 text-base font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800 focus:bg-gray-100 focus:text-gray-800 focus:outline-none transition duration-150 ease-in-out"
            onClick={closeMobileMenu}
          >
            Account
          </Link>
          <button
            type="button"
            onClick={handleLogout}
            className="mt-1 block w-full px-4 py-2 text-left text-base font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800 focus:bg-gray-100 focus:text-gray-800 focus:outline-none transition duration-150 ease-in-out"
          >
            Sign out
          </button>
        </div>
      </div>
    </nav>
  );
};
